import React, { useState } from "react";
import { appendLeadSource } from "../../lib/leadSource";
import axios from "axios";
import { Camera, X, Loader2, CheckCircle2 } from "lucide-react";
import { PHOTO_SHOTS } from "../../data/mock";
import { PriceMatchUpload } from "../PriceMatchUpload";
import { useCurrentPromo } from "../../lib/promo";
import { useContent } from "../../lib/content";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "../ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ExampleThumb = ({ i, label }) => (
  <Dialog>
    <DialogTrigger asChild>
      <button type="button" data-testid={`photo-slot-${i + 1}-example`} aria-label={`See example: ${label}`} className="relative w-14 h-14 shrink-0 rounded-lg overflow-hidden ring-1 ring-slate-200 hover:ring-[#3b9dff] transition-shadow">
        <img src={`/examples/shot-${i + 1}.webp`} alt="" className="w-full h-full object-cover" loading="lazy" />
        <span className="absolute inset-x-0 bottom-0 bg-[#0a1f44]/75 text-[9px] font-bold uppercase tracking-wide text-white text-center py-0.5">Example</span>
      </button>
    </DialogTrigger>
    <DialogContent className="sm:max-w-[520px]" data-testid={`photo-slot-${i + 1}-example-dialog`}>
      <DialogTitle className="text-lg font-bold text-[#0a1f44]">Shot {i + 1}: {label}</DialogTitle>
      <img src={`/examples/shot-${i + 1}.webp`} alt={`Example photo: ${label}`} className="w-full rounded-xl" />
      <p className="text-xs text-slate-500">Example image to show the angle. Your photo does not need to look exactly like this.</p>
    </DialogContent>
  </Dialog>
);

const ShotSlot = ({ i, label, file, onPick, onClear }) => {
  const preview = file ? URL.createObjectURL(file) : null;
  return (
    <li data-testid={`photo-slot-${i + 1}`} className={`flex items-center gap-3 rounded-xl border p-2.5 transition-colors ${file ? "border-emerald-200 bg-emerald-50/50" : "border-slate-200"}`}>
      <ExampleThumb i={i} label={label} />
      <span className="flex-1 min-w-0 text-sm">
        <span className="block font-semibold text-[#0a1f44] leading-snug">{i + 1}. {label}</span>
        <span className="block text-xs text-slate-500 truncate">{file ? file.name : "Not added yet"}</span>
      </span>
      {file ? (
        <>
          <img src={preview} alt="" onLoad={() => URL.revokeObjectURL(preview)} data-testid={`photo-slot-${i + 1}-preview`} className="w-11 h-11 rounded-lg object-cover ring-2 ring-emerald-300" />
          <button type="button" data-testid={`photo-slot-${i + 1}-remove`} onClick={onClear} aria-label="Remove photo" className="p-2 rounded-full hover:bg-white"><X className="w-4 h-4 text-slate-500" /></button>
        </>
      ) : (
        <label className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#3b9dff] hover:bg-[#2b8ae8] text-white text-xs font-bold cursor-pointer transition-colors">
          <Camera className="w-3.5 h-3.5" /> Add
          <input type="file" accept="image/*,.heic,.heif" data-testid={`photo-slot-${i + 1}-input`} className="hidden"
            onChange={(e) => { if (e.target.files[0]) onPick(e.target.files[0]); e.target.value = ""; }} />
        </label>
      )}
    </li>
  );
};

const PhotoPicker = ({ slots, setSlot }) => (
  <div>
    <p className="font-bold text-[#0a1f44]">Add a photo for each shot</p>
    <p className="text-xs text-slate-500">Tap an example to see the angle. JPG, PNG, WEBP, or HEIC, up to 15 MB each.</p>
    <ol className="mt-3 space-y-2">
      {PHOTO_SHOTS.map((label, i) => (
        <ShotSlot key={label} i={i} label={label} file={slots[i]} onPick={(f) => setSlot(i, f)} onClear={() => setSlot(i, null)} />
      ))}
    </ol>
  </div>
);

const Field = (props) => <input {...props} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#3b9dff] focus:outline-none" />;

export const PhotoQuoteForm = ({ source = "home" }) => {
  const { quoting } = useContent();
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [slots, setSlots] = useState(PHOTO_SHOTS.map(() => null));
  const filled = slots.filter(Boolean).length;
  const missing = PHOTO_SHOTS.filter((_, i) => !slots[i]);
  const setSlot = (i, f) => { setSlots((cur) => cur.map((x, j) => (j === i ? f : x))); setState((st) => ({ ...st, error: "" })); };
  const [state, setState] = useState({ sending: false, done: false, error: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!filled) return setState({ ...state, error: "Please add at least one photo." });
    setState({ sending: true, done: false, error: "" });
    const fd = new FormData();
    const shotList = PHOTO_SHOTS.map((l, i) => `${i + 1}. ${l}: ${slots[i] ? "included" : "missing"}`).join("\n");
    Object.entries(form).forEach(([k, v]) => fd.append(k, k === "message" ? `[${source}] ${v}\n\nShots (photos below are in this order):\n${shotList}` : v));
    slots.forEach((f) => f && fd.append("photos", f));
    try {
      await axios.post(`${API}/photo-quote`, appendLeadSource(fd));
      setState({ sending: false, done: true, error: "" });
    } catch (err) {
      setState({ sending: false, done: false, error: (typeof err.response?.data?.detail === "string" && err.response.data.detail) || "Upload failed. Please try again or text (252) 545-0011." });
    }
  };

  if (state.done) return (
    <div data-testid="photo-quote-success" className="rounded-2xl bg-white border border-slate-200 p-8 text-center">
      <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
      <p className="mt-3 text-xl font-bold text-[#0a1f44]">Got your photos.</p>
      <p className="mt-1 text-slate-600">We will send your itemized quote by text or call.</p>
    </div>
  );

  return (
    <form onSubmit={submit} data-testid="photo-quote-form" className="rounded-2xl bg-white border border-slate-200 p-6 md:p-8 space-y-4">
      <PhotoPicker slots={slots} setSlot={setSlot} />
      {filled > 0 && missing.length > 0 && (
        <p data-testid="photo-quote-missing-reminder" className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
          You've added {filled} of {PHOTO_SHOTS.length} shots. Still missing: {missing.join("; ")}. You can still send now.
        </p>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        <Field required placeholder="Name *" value={form.name} onChange={set("name")} data-testid="photo-quote-name" />
        <Field required type="tel" placeholder="Phone *" value={form.phone} onChange={set("phone")} data-testid="photo-quote-phone" />
      </div>
      <Field type="email" placeholder="Email (optional)" value={form.email} onChange={set("email")} data-testid="photo-quote-email" />
      <textarea rows={3} placeholder="Wall width, ceiling height, seating distance, TV size, or anything we should know (optional)" value={form.message} onChange={set("message")} data-testid="photo-quote-message"
        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#3b9dff] focus:outline-none" />
      {state.error && <p data-testid="photo-quote-error" className="text-sm text-red-600">{state.error}</p>}
      <button type="submit" disabled={state.sending} data-testid="photo-quote-submit" className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full bg-[#3b9dff] hover:bg-[#2b8ae8] text-white font-bold disabled:opacity-60 transition-colors">
        {state.sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading photos</> : "Send my photos for a quote"}
      </button>
      <p data-testid="photo-quote-survey-note" className="text-sm text-slate-500 text-center">{quoting.surveyNote}</p>
    </form>
  );
};

export const PhotoQuoteSection = ({ source }) => {
  const promo = useCurrentPromo();
  return (
  <section id="photo-quote" data-testid="photo-quote-section" className="section-pad bg-white tex-grid-light">
    <div className="max-w-[1100px] mx-auto px-5 lg:px-8 grid lg:grid-cols-[1fr_1.3fr] gap-10 items-start">
      <div className="lg:sticky lg:top-28">
        <span className="eyebrow">On a computer?</span>
        <h2 className="mt-3 text-3xl md:text-4xl font-extrabold text-[#0a1f44]">Upload a photo of your room</h2>
        <p className="mt-4 text-slate-600 leading-relaxed">Same as texting us. Send a photo of the room and the wall, and we send an itemized quote with the exact flat-rate install price. You approve it before we book.</p>
        <div data-testid="photo-quote-checklist" className="mt-6 rounded-2xl bg-white border border-slate-200 p-6">
          <p className="font-bold text-[#0a1f44]">One photo per shot</p>
          <p className="mt-2 text-sm text-slate-600">Each of the 5 shots we need has its own spot in the form, so nothing gets missed.</p>
          <p className="mt-3 text-sm text-slate-500">Plus your wall width, ceiling height, and seating distance. Add them in the notes box.</p>
        </div>
      </div>
      <div className="space-y-5">
        <PhotoQuoteForm source={source} />
        {source === "home" && promo?.match_beat && <PriceMatchUpload source="home" promo={promo} />}
      </div>
    </div>
  </section>
  );
};
